import React, { useState, useEffect } from 'react';

const ChatSidebarTypingIndicator = ({ 
  darkMode, 
  onStop, 
  isPolling = false, 
  currentProgress = 0, 
  currentProgressMessage = '' 
}) => {
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    const startTime = Date.now();
    const timer = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => {
      clearInterval(timer);
      setElapsedTime(0);
    };
  }, []);

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className="flex justify-start">
      <div className={`
        rounded-2xl px-4 py-3 shadow-sm relative
        ${darkMode 
          ? 'bg-gray-800 border border-gray-700' 
          : 'bg-gray-100'
        }
      `}>
        <div className="flex items-center gap-2">
          <div className={`flex gap-1 ${darkMode ? 'text-gray-400' : 'text-purple-500'}`}>
            <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
          <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {isPolling && currentProgressMessage ? currentProgressMessage : 'Thinking...'}
          </span>
          
          {/* Stop button */}
          {onStop && (
            <button
              onClick={onStop}
              className={`
                ml-2 p-1 rounded-lg transition-colors text-xs
                ${darkMode 
                  ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' 
                  : 'hover:bg-gray-200 text-gray-500 hover:text-gray-700'
                }
              `}
              title="Stop thinking"
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          )}
        </div>
        
        {/* Progress bar for polling mode */}
        {isPolling && currentProgress > 0 && (
          <div className="mt-2">
            <div className={`w-full bg-gray-200 rounded-full h-1.5 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <div 
                className="bg-purple-500 h-1.5 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${Math.min(currentProgress, 100)}%` }}
              />
            </div>
            <div className={`text-xs mt-1 text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {Math.round(currentProgress)}%
            </div>
          </div>
        )}
        
        {/* Timer underneath (for non-polling mode) */}
        {!isPolling && (
          <div className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {formatTime(elapsedTime)}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatSidebarTypingIndicator;
